import apiClient from "./apiClient";
import { supabase } from "./supabaseClient";
import {  generateSessionId, getSessionId, getToken } from "../utils/storage";



export interface StartPaidExamResponse {
  success: boolean;
  exam_id: string;
  message: string;
  goal_at_time_of_exam?: string | null;
}
export interface StartPaidExamRequest {
  goal_level?: string | null;
  session_id?: string | null;
}


//added for guest exam start
export interface StartGuestExamResponse {
  user_id: string;
  is_guest: boolean; // <-- updated to match edge function
  token: string | null; // <-- token returned by supabase.auth.signInAnonymously
  message: string;
  last_session_id?: string | null; // <-- added to track the session ID
}


export function getStoredGuestExamUser(): StartGuestExamResponse | null {
  const data = localStorage.getItem("guestExamUser");
  return data ? JSON.parse(data) : null;
}


export async function startGuestExam(): Promise<StartGuestExamResponse> {
  const { data: anonData, error } = await supabase.auth.signInAnonymously();

  if (error || !anonData?.user) {
    console.error("Error during anonymous sign-in:", error);
    throw new Error("Failed to create guest user: " + (error?.message || "Unknown error"));
  }

  console.log("Anonymous user created:", anonData.user.id);

  const guestexamId = generateSessionId(); // custom function you already have
  console.log("Using session ID for guest exam:", guestexamId);

  // Create/Update user in "users" table
  try {
    const userData = {
      id: anonData.user.id,
      email: anonData.user.email && anonData.user.email.trim() !== "" 
        ? anonData.user.email 
        : null,
      created_at: new Date().toISOString(),
      is_guest: true,
    // Store the session ID with the user
    };

    console.log("Attempting to upsert user data:", userData);

    const { error: upsertError } = await supabase
      .from("users")
      .upsert(userData, { onConflict: "id", ignoreDuplicates: false });

    if (upsertError) {
      console.error("Error upserting guest user:", upsertError);
    } else {
      console.log("✅ Guest user upserted successfully");
    }

    // ✅ Insert into exam table
    const examData = {
      id: guestexamId,
      user_id: anonData.user.id,
      created_at: new Date().toISOString(),
    };

    const { error: examInsertError } = await supabase
      .from("exams")
      .insert(examData);

    if (examInsertError) {
      console.error("❌ Error inserting exam record:", examInsertError);
    } else {
      console.log("✅ Guest exam record inserted:", examData);
    }

  } catch (err) {
    console.error("Unexpected error in user creation/exam insertion:", err);
  }

  // Build response
  const response: StartGuestExamResponse = {
    user_id: anonData.user.id,
    is_guest: true,
    token: anonData.session?.access_token ?? null,
    message: "Guest user & exam created successfully",
    last_session_id: guestexamId, 

  };

  // Store in localStorage
  try {
    localStorage.setItem("guestExamUser", JSON.stringify(response));
     if (response.token) {
      localStorage.setItem("guestToken", response.token);
    }
    console.log("📦 Stored guest exam user in localStorage:", response);
  } catch (err) {
    console.error("Failed to store guest user in localStorage:", err);
  }

  return response;
}


export async function startPaidExam(
  request?: StartPaidExamRequest
): Promise<StartPaidExamResponse> {
  const { data } = await apiClient.post<StartPaidExamResponse>(
    "/startPaidExam",
    request ?? {}
  );
  return data;
}

// Task attempt functions
export interface CreateTaskAttemptRequest {
  sessionId: string;
  taskType: number;
}

export interface CreateTaskAttemptResponse {
  success: boolean;
  taskAttemptId?: string;
  taskId?: string;
  examId?: string;
  taskType?: number;
  topicId?: string;
  attemptNumber?: number;
  message: string;
}

export async function createTaskAttempt(
  request: CreateTaskAttemptRequest
): Promise<CreateTaskAttemptResponse> {
  try {
    // 1) Get tokens
    const paidToken = getToken();
    const guestUser = getStoredGuestExamUser();
    const guestToken = guestUser?.token ?? localStorage.getItem("guestToken");

    // 2) Decide which mode
    const isGuest = !paidToken && !!guestToken;
    const token = paidToken || guestToken;

    if (!token) {
      throw new Error('No paid or guest authentication token available');
    }

    // 3) Build payload
    const payload: any = { ...request };
    if (isGuest) {
      payload.guest_id = guestUser?.user_id;
      payload.is_guest = true;
    }

    const response = await fetch('https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/create_task_attempt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create task attempt: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating task attempt:', error);
    throw error;
  }
}

// Update task attempt with scoring data
export interface UpdateTaskAttemptRequest {
  taskAttemptId: string;
  transcript?: string;
  grammar?: number;
  vocabulary?: number;
  comprehension?: number;
  fluency?: number;
  pronunciation?: number;
  averageScore?: number;
  estimatedCefr?: string;
  strength?: string;
  weakness?: string;
  grammarTips?: string;
  vocabTips?: string;
  rewrittenResponse?: string;
}

export interface UpdateTaskAttemptResponse {
  success: boolean;
  taskAttemptId?: string;
  taskId?: string;
  examId?: string;
  taskType?: number;
  message: string;
}

export async function updateTaskAttempt(
  request: UpdateTaskAttemptRequest
): Promise<UpdateTaskAttemptResponse> {
  try {
    // 1) Get tokens
    const paidToken = getToken();
    const guestUser = getStoredGuestExamUser();
    const guestToken = guestUser?.token ?? localStorage.getItem("guestToken");

    // 2) Decide which mode
    const isGuest = !paidToken && !!guestToken;
    const token = paidToken || guestToken;

    if (!token) {
      throw new Error('No paid or guest authentication token available');
    }

    // 3) Build payload
    const payload: any = { ...request };
    if (isGuest) {
      payload.guest_id = guestUser?.user_id;
      payload.is_guest = true;
    }

    const response = await fetch('https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/update_task_attempt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update task attempt: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating task attempt:', error);
    throw error;
  }
}
